import { useEffect, useRef, useState } from 'react';
import {
  Spinner, WarningCircle, CloudArrowDown, GoogleLogo,
  CheckCircle, FolderSimple, X, CaretDown, CaretRight,
  ArrowsClockwise, Trash, PaperPlaneTilt, Clock, Eye, PencilSimple,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useDriveStore, parseDriveFolderUrl } from '../../stores/driveStore';
import { useAssetStore } from '../../stores/assetStore';
import { useSubmissionStore } from '../../stores/submissionStore';
import SubmitReviewDialog from '../review/SubmitReviewDialog';
import type { DriveSpineSet, Asset, Submission } from '@spine-workspace/shared';
import type { SavedDriveFolder } from '../../stores/driveStore';

interface DriveBrowserProps {
  onDragAsset?: (asset: Asset) => void;
  onDoubleClickAsset?: (asset: Asset) => void;
}

export default function DriveBrowser({ onDragAsset, onDoubleClickAsset }: DriveBrowserProps) {
  const {
    connected, checking,
    savedFolders,
    pickedFolder, loadingFolder, folderError,
    importing, importError,
    checkConnection, connectDrive, inspectFolder, clearPicked, importSpineSet,
    fetchSavedFolders,
  } = useDriveStore();
  const { fetchAssets, assets, deleteAsset } = useAssetStore();
  const { submissions, submitForReview, fetchSubmissions: fetchSubs } = useSubmissionStore();

  // Load submission states so we can show status on saved folders
  useEffect(() => { fetchSubs(); }, []);

  const [urlInput, setUrlInput] = useState('');
  const [inputError, setInputError] = useState('');
  const [importingSetId, setImportingSetId] = useState<string | null>(null);
  const [importedSetIds, setImportedSetIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const [submitDialogTarget, setSubmitDialogTarget] = useState<{
    folder: SavedDriveFolder;
    assets: Asset[];
    existing: Submission | null;
  } | null>(null);

  useEffect(() => {
    checkConnection();
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      checkConnection();
    }
  }, []);

  function handleCheck() {
    setInputError('');
    const parsed = parseDriveFolderUrl(urlInput);
    if (!parsed) {
      setInputError('Paste a Google Drive folder link or a folder ID');
      return;
    }
    inspectFolder(parsed.folderId, parsed.folderName);
  }

  async function handleImportSet(set: DriveSpineSet) {
    const setKey = set.json.id; // unique per set even in multi-set folders
    setImportingSetId(setKey);
    const jsonStem = set.json.name.replace(/\.json$/i, '').trim();
    const folderName = jsonStem || set.folderName || set.folderId;
    try {
      await importSpineSet({
        folderId: set.folderId,
        folderName,
        jsonFileId: set.json.id,
        atlasFileId: set.atlas.id,
        pngFileIds: set.pngs.map(p => p.id),
      });
      await Promise.all([fetchAssets(), fetchSavedFolders()]);
      setImportedSetIds(prev => new Set(prev).add(setKey));
      setTimeout(() => {
        setImportedSetIds(prev => {
          const next = new Set(prev);
          next.delete(setKey);
          return next;
        });
      }, 4000);
    } finally {
      setImportingSetId(null);
    }
  }

  // ── Loading ──────────────────────────────────────────────
  if (checking || connected === null) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner size={16} className="animate-spin text-text-tertiary" />
      </div>
    );
  }

  // ── Not connected ────────────────────────────────────────
  if (!connected) {
    return (
      <div className="p-5 flex flex-col items-center gap-3 text-center">
        <GoogleLogo size={28} weight="light" className="text-text-tertiary" />
        <p className="text-xs text-text-secondary leading-relaxed">
          Connect Google Drive to import Spine assets directly
        </p>
        <button
          onClick={connectDrive}
          className="px-3 py-1.5 rounded text-xs font-sans bg-accent text-white hover:bg-accent/80 transition-colors"
        >
          Connect Google Drive
        </button>
      </div>
    );
  }

  // ── Connected ────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-3 p-3 overflow-y-auto h-full">

      {/* Saved Folders */}
      {savedFolders.length > 0 && (
        <div className="space-y-1">
          <p className="text-[9px] font-mono uppercase text-text-tertiary tracking-wide">
            Saved Folders
          </p>
          {savedFolders.map((sf) => {
            const sfKey = `${sf.folderId}:${sf.folderName}`;
            const folderAssets = sf.assetIds
              .map(id => assets.find(a => a.id === id))
              .filter((a): a is Asset => Boolean(a));
            const submission = submissions.find(s => s.driveFolderId === sf.folderId) ?? null;

            // Sync: re-import latest Drive content as a new version for each asset
            async function handleSync() {
              const jsonStem = folderAssets[0]?.name ?? sf.folderName;
              await Promise.all(
                (sf.assetIds.length > 0 ? sf.assetIds : [undefined]).map((assetId) =>
                  importSpineSet({ folderId: sf.folderId, folderName: jsonStem, assetId })
                )
              );
              await Promise.all([fetchAssets(), fetchSavedFolders()]);
            }

            // Delete: remove all assets tied to this folder, then refresh
            async function handleDelete() {
              await Promise.all(folderAssets.map(a => deleteAsset(a.id)));
              await fetchSavedFolders();
            }

            // Submit for review — opens confirmation dialog
            function handleSubmitForReview() {
              setSubmitDialogTarget({
                folder: sf,
                assets: folderAssets,
                existing: submission,
              });
            }

            return (
              <SavedFolderRow
                key={sfKey}
                folderId={sf.folderId}
                folderName={sf.folderName}
                assets={folderAssets}
                submission={submission}
                onDragAsset={onDragAsset}
                onDoubleClickAsset={onDoubleClickAsset}
                onSync={handleSync}
                onDelete={handleDelete}
                onSubmitForReview={handleSubmitForReview}
              />
            );
          })}
        </div>
      )}

      {/* Paste URL input */}
      <div className="space-y-1.5">
        <label className="text-[9px] font-mono uppercase text-text-tertiary tracking-wide">
          Paste a Drive folder link
        </label>
        <div className="flex gap-1.5">
          <input
            ref={inputRef}
            value={urlInput}
            onChange={(e) => { setUrlInput(e.target.value); setInputError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
            placeholder="drive.google.com/drive/folders/…"
            className="flex-1 min-w-0 bg-bg-input border border-border rounded px-2 py-1.5 text-[10px] font-mono text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors"
          />
          <button
            onClick={handleCheck}
            disabled={!urlInput.trim() || loadingFolder}
            className="shrink-0 px-2.5 py-1.5 rounded text-[10px] font-sans bg-accent/15 text-accent hover:bg-accent/25 disabled:opacity-40 transition-colors"
          >
            {loadingFolder ? <Spinner size={10} className="animate-spin" /> : 'Check'}
          </button>
        </div>

        {/* Input parse error */}
        {inputError && (
          <p className="text-[9px] text-red-400 flex items-center gap-1">
            <WarningCircle size={10} weight="fill" />
            {inputError}
          </p>
        )}
      </div>

      {/* Folder inspect error */}
      {folderError && (!pickedFolder || pickedFolder.spineSets.length === 0) && (
        <div className="flex items-start gap-1.5 text-[10px] text-amber-400 bg-amber-400/10 rounded px-2 py-1.5">
          <WarningCircle size={11} weight="fill" className="shrink-0 mt-px" />
          <span>{folderError}</span>
        </div>
      )}

      {/* Import API error */}
      {importError && (
        <div className="flex items-start gap-1.5 text-[10px] text-red-400 bg-red-400/10 rounded px-2 py-1.5">
          <WarningCircle size={11} weight="fill" className="shrink-0 mt-px" />
          {importError}
        </div>
      )}

      {/* Spine set result cards */}
      {pickedFolder && (
        <div className="space-y-2">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-mono uppercase text-text-tertiary tracking-wide">
              {pickedFolder.spineSets.length > 1
                ? `${pickedFolder.spineSets.length} Spine Sets Found`
                : pickedFolder.spineSets.length === 1
                  ? 'Spine Set Found'
                  : 'No Spine Sets Found'}
            </span>
            <button
              onClick={clearPicked}
              className="p-0.5 rounded text-text-tertiary hover:text-text-primary transition-colors"
            >
              <X size={10} />
            </button>
          </div>

          {pickedFolder.spineSets.length === 0 ? (
            <div className="space-y-1">
              <p className="text-[10px] text-text-tertiary px-1">
                No Spine export found. Files seen:
              </p>
              {(pickedFolder.debugFiles ?? []).map((f, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[9px] font-mono text-text-tertiary px-1">
                  <span className="truncate">{f.name}</span>
                  <span className="shrink-0 text-text-tertiary/50">({f.mimeType})</span>
                </div>
              ))}
              {(pickedFolder.debugFiles ?? []).length === 0 && (
                <p className="text-[9px] font-mono text-text-tertiary px-1">— folder appears empty</p>
              )}
            </div>
          ) : (
            pickedFolder.spineSets.map((set) => {
              const key = set.json.id;
              const isImporting = importingSetId === key;
              const wasImported = importedSetIds.has(key);
              return (
                <SpineSetCard
                  key={key}
                  set={set}
                  importing={isImporting || (importing && importingSetId === key)}
                  imported={wasImported}
                  onImport={() => handleImportSet(set)}
                />
              );
            })
          )}
        </div>
      )}

      {/* Help hint */}
      {!pickedFolder && !folderError && savedFolders.length === 0 && (
        <p className="text-[9px] text-text-tertiary leading-relaxed">
          Open a Spine export folder in Google Drive, copy its link, and paste it above.
        </p>
      )}

      {/* Submit for Review dialog */}
      {submitDialogTarget && (
        <SubmitReviewDialog
          open={Boolean(submitDialogTarget)}
          onClose={() => setSubmitDialogTarget(null)}
          folderId={submitDialogTarget.folder.folderId}
          folderName={submitDialogTarget.folder.folderName}
          assets={submitDialogTarget.assets}
          existingSubmission={submitDialogTarget.existing}
          onConfirm={async ({ name, note }) => {
            await submitForReview({
              driveFolderId: submitDialogTarget.folder.folderId,
              driveFolderName: submitDialogTarget.folder.folderName,
              assetId: submitDialogTarget.folder.assetIds[0],
              name,
              note,
            });
            await fetchSubs();
          }}
        />
      )}
    </div>
  );
}

// ── SavedFolderRow ────────────────────────────────────────────

const STATUS_ICON: Record<string, React.ReactNode> = {
  waiting_for_review: <Clock size={9} weight="fill" className="text-amber-400" />,
  in_review:          <Eye size={9} weight="fill" className="text-blue-400" />,
  need_update:        <PencilSimple size={9} weight="fill" className="text-red-400" />,
  approved:           <CheckCircle size={9} weight="fill" className="text-emerald-400" />,
};
const STATUS_LABEL: Record<string, string> = {
  waiting_for_review: 'Waiting',
  in_review: 'In Review',
  need_update: 'Needs Update',
  approved: 'Approved',
};

interface SavedFolderRowProps {
  folderId: string;
  folderName: string;
  assets: Asset[];
  submission: Submission | null;
  onDragAsset?: (asset: Asset) => void;
  onDoubleClickAsset?: (asset: Asset) => void;
  onSync: () => Promise<void>;
  onDelete: () => Promise<void>;
  onSubmitForReview: () => void;
}

function SavedFolderRow({
  folderId, folderName, assets, submission,
  onDragAsset, onDoubleClickAsset, onSync, onDelete, onSubmitForReview,
}: SavedFolderRowProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const displayName = folderName || `Folder ${folderId.slice(0, 8)}…`;

  async function handleSync(e: React.MouseEvent) {
    e.stopPropagation();
    setSyncing(true);
    try {
      await onSync();
      setSyncDone(true);
      setTimeout(() => setSyncDone(false), 3000);
    } finally {
      setSyncing(false);
    }
  }

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    setDeleting(true);
    try { await onDelete(); } finally { setDeleting(false); }
  }

  function handleSubmit(e: React.MouseEvent) {
    e.stopPropagation();
    onSubmitForReview();
  }

  function handleDragStart(e: React.DragEvent, asset: Asset) {
    e.dataTransfer.setData('application/spine-asset', JSON.stringify([asset]));
    e.dataTransfer.effectAllowed = 'copy';
    onDragAsset?.(asset);
  }

  return (
    <div className="rounded bg-bg-input border border-border overflow-hidden">
      <div
        className="group relative flex items-center gap-1.5 px-2 py-1.5 cursor-pointer hover:bg-bg-hover transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        {expanded
          ? <CaretDown size={9} className="text-text-tertiary shrink-0" />
          : <CaretRight size={9} className="text-text-tertiary shrink-0" />}
        <FolderSimple size={11} className="text-amber-400 shrink-0" />
        <span className="text-[10px] font-sans text-text-primary truncate flex-1 min-w-0" title={displayName}>
          {displayName}
        </span>

        {/* Submission status badge */}
        {submission && (
          <button
            title={`${STATUS_LABEL[submission.status]} — click to open review`}
            onClick={e => { e.stopPropagation(); if (submission.reviewSceneId) navigate(`/scene/${submission.reviewSceneId}/edit`); }}
            className="flex items-center gap-0.5 shrink-0 group-hover:opacity-0 transition-opacity"
          >
            {STATUS_ICON[submission.status]}
          </button>
        )}

        {/* Action buttons — visible on hover */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 absolute right-2">
          <button
            title={submission ? 'Re-submit for Review' : 'Submit for Review'}
            onClick={handleSubmit}
            className="p-1 rounded text-text-tertiary hover:text-accent hover:bg-accent/10 transition-colors"
          >
            <PaperPlaneTilt size={10} />
          </button>
          <button
            title={syncDone ? 'Synced!' : 'Sync latest from Drive'}
            onClick={handleSync}
            disabled={syncing}
            className={`p-1 rounded transition-colors ${
              syncDone ? 'text-emerald-400' : 'text-text-tertiary hover:text-accent hover:bg-accent/10'
            }`}
          >
            {syncing ? <Spinner size={10} className="animate-spin" />
              : syncDone ? <CheckCircle size={10} weight="fill" />
              : <ArrowsClockwise size={10} />}
          </button>
          <button
            title="Remove folder"
            onClick={handleDelete}
            disabled={deleting}
            className="p-1 rounded text-text-tertiary hover:text-red-400 hover:bg-red-400/10 transition-colors"
          >
            {deleting ? <Spinner size={10} className="animate-spin" /> : <Trash size={10} />}
          </button>
        </div>

        <span className="text-[9px] font-mono text-text-tertiary shrink-0 ml-1 group-hover:opacity-0 transition-opacity">
          {assets.length}
        </span>
      </div>

      {expanded && assets.length > 0 && (
        <div className="border-t border-border-subtle">
          {assets.map((asset) => (
            <div
              key={asset.id}
              draggable
              title="Double-click to add to scene, or drag onto canvas"
              onDragStart={(e) => handleDragStart(e, asset)}
              onDoubleClick={() => onDoubleClickAsset?.(asset)}
              className="flex items-center gap-2 px-2 py-[3px] mx-1 my-0.5 rounded cursor-pointer select-none hover:bg-bg-hover transition-colors"
            >
              <span className="text-[9px] font-mono text-text-tertiary w-8 shrink-0">
                {asset.spineVersion ?? '—'}
              </span>
              <span className="text-[10px] font-sans text-text-primary truncate flex-1">
                {asset.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {expanded && assets.length === 0 && (
        <p className="px-2 py-1.5 text-[9px] text-text-tertiary border-t border-border-subtle">
          No assets imported from this folder yet
        </p>
      )}
    </div>
  );
}

// ── SpineSetCard ─────────────────────────────────────────────

interface SpineSetCardProps {
  set: DriveSpineSet;
  importing: boolean;
  imported: boolean;
  onImport: () => void;
}

function SpineSetCard({ set, importing, imported, onImport }: SpineSetCardProps) {
  const displayName = set.folderName && set.folderName !== set.folderId
    ? set.folderName
    : `Folder ${set.folderId.slice(0, 8)}…`;

  return (
    <div className="rounded-md bg-bg-input border border-border overflow-hidden">
      {/* Folder header */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border-subtle">
        <FolderSimple size={11} className="text-amber-400 shrink-0" />
        <span className="text-[10px] font-sans text-text-primary truncate flex-1" title={displayName}>
          {displayName}
        </span>
        {imported && (
          <CheckCircle size={11} weight="fill" className="text-emerald-400 shrink-0" />
        )}
      </div>

      <div className="p-2 space-y-2">
        {/* Detected badge */}
        <span className="text-[9px] font-mono font-semibold uppercase text-accent tracking-wide">
          Spine Export Detected
        </span>

        {/* File list */}
        <div className="space-y-0.5">
          {[set.json, set.atlas, ...set.pngs].map((f) => {
            const ext = f.name.endsWith('.json') ? '.json'
              : f.name.endsWith('.atlas') ? '.atlas' : '.png';
            return (
              <div key={f.id} className="flex items-center gap-1.5 text-[9px] font-mono text-text-secondary">
                <span className="w-9 shrink-0 text-text-tertiary">{ext}</span>
                <span className="truncate">{f.name}</span>
              </div>
            );
          })}
        </div>

        {/* Import button */}
        {imported ? (
          <div className="flex items-center justify-center gap-1.5 py-1.5 text-[10px] font-sans text-emerald-400">
            <CheckCircle size={11} weight="fill" />
            Imported
          </div>
        ) : (
          <button
            onClick={onImport}
            disabled={importing}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] font-sans bg-accent/15 text-accent hover:bg-accent/25 disabled:opacity-50 transition-colors"
          >
            {importing
              ? <Spinner size={10} className="animate-spin" />
              : <CloudArrowDown size={11} weight="fill" />
            }
            {importing ? 'Importing…' : 'Import as new asset'}
          </button>
        )}
      </div>
    </div>
  );
}
